import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { buildGame, getInventory, getPlan, tryTogether } from '../api/gameBuilderAPI';
import MissionGame from './MissionGame';
import SitePlayer from './SitePlayer';
import StorylinePlayer from './StorylinePlayer';
import { playableChain } from './chain';
import './GameBuilder.css';

/**
 * The Game Builder's players, each in its own browser window/tab (not a dialog over the builder):
 *   TryOutPage  /game-builder/try?items=a,b,c  - inventory items tried out (one: its sample; more: side by side)
 *   PlayPage    /game-builder/play/:planId/:missionId - a mission's game (built first if it isn't yet)
 *   PlanPlayPage /game-builder/story/:planId and /game-builder/site/:planId - the plan's storyline, or its site map
 * Closing the game closes the window (or goes back to the builder when the browser won't let a script close it).
 */
const leave = (navigate) => () => {
  window.close();
  setTimeout(() => navigate('/app/studio'), 150);          // still here: the tab wasn't opened by the builder
};

function Waiting({ text, error }) {
  return (
    <main className="gb-guest">
      {error
        ? <div className="gb-panel" role="alert"><h2>Couldn't open it</h2><p>{error}</p></div>
        : <p className="gb-muted"><span className="gb-spinner" /> {text}</p>}
    </main>
  );
}

export function TryOutPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const ids = (params.get('items') || '').split(',').map((x) => x.trim()).filter(Boolean);
  const key = ids.join(',');
  const [spec, setSpec] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    const load = ids.length === 1
      ? getInventory().then((d) => {
        const item = d.items.find((it) => it.id === ids[0]);
        if (!item?.try) throw new Error(`No item "${ids[0]}" to try`);
        return item.try;
      })
      : tryTogether(ids);
    load.then((s) => live && setSpec(s)).catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!ids.length) return <Waiting error="No items to try." />;
  if (!spec) return <Waiting text="Setting up…" error={error} />;
  return <MissionGame spec={spec} tryout page onClose={leave(navigate)} />;
}

export function PlayPage() {
  const { planId, missionId } = useParams();
  const navigate = useNavigate();
  const [spec, setSpec] = useState(null);
  const [error, setError] = useState('');
  const [note, setNote] = useState('Opening the simulation…');
  useEffect(() => {
    let live = true;
    buildGame(planId, missionId, (status) => live && setNote(status === 'queued' ? 'Waiting to build the simulation…' : 'Building the simulation…'))
      .then((s) => live && setSpec(s)).catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [planId, missionId]);
  if (!spec) return <Waiting text={note} error={error} />;
  return <MissionGame spec={spec} allowShift page onClose={leave(navigate)} />;
}

/** A plan's storyline (missions one after another) or its site map (rooms to walk between), in its own window. */
export function PlanPlayPage({ what }) {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    getPlan(planId).then((p) => live && setPlan(p)).catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [planId]);
  if (!plan) return <Waiting text="Opening…" error={error} />;
  if (what === 'site') {
    return plan.site ? <SitePlayer plan={plan} page onClose={leave(navigate)} /> : <Waiting error="This plan has no site map yet." />;
  }
  return playableChain(plan) ? <StorylinePlayer plan={plan} page onClose={leave(navigate)} /> : <Waiting error="This plan has no simulation painted yet." />;
}
